import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { ToastContainer, toast } from 'react-toastify';
import { FaCheck, FaTimes, FaEye, FaTrash, FaToggleOn, FaToggleOff, FaThumbsUp } from 'react-icons/fa';
import 'react-toastify/dist/ReactToastify.css';
import PublisherApiService from '../../ApiServices/PublisherApiService/PublisherApiService';

const PublishersTable = () => {
    const [allPublishers, setAllPublishers] = useState([]);
    const [filteredPublishers, setFilteredPublishers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedPublisher, setSelectedPublisher] = useState(null);

    const loadInit = async () => {
        setLoading(true);
        try {
            const response = await PublisherApiService.get();
            setAllPublishers(response.data);
            setFilteredPublishers(response.data);
        } catch (error) {
            console.error(error);
            toast.error('Failed to fetch publishers.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadInit();
    }, []);

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (!value.trim()) {
            setFilteredPublishers(allPublishers);
        } else {
            const lowerValue = value.toLowerCase();
            const filtered = allPublishers.filter(p =>
                p.name.toLowerCase().includes(lowerValue) ||
                p.email.toLowerCase().includes(lowerValue) ||
                p.company.toLowerCase().includes(lowerValue) ||
                p.phone.includes(value)
            );
            setFilteredPublishers(filtered);
        }
    };

    const toggleInvoiceStatus = async (id, currentStatus) => {
        try {
            await PublisherApiService.updateInvoice(id);
            loadInit();
            toast.success('Invoice status updated.');
        } catch (error) {
            console.error(error);
            toast.error('Failed to update invoice status.');
        }
    };

    const approvePublisher = async (id) => {
        try {
            await PublisherApiService.ApprovePublisher(id);
            loadInit();
            toast.success('Publisher approved.');
        } catch (error) {
            console.error(error);
            toast.error('Approval failed.');
        }
    };

    const deletePublisher = async (id) => {
        if (window.confirm('Delete this publisher?')) {
            try {
                await PublisherApiService.DeletePublisher(id);
                loadInit();
                toast.success('Publisher deleted.');
            } catch (error) {
                console.error(error);
                toast.error('Failed to delete publisher.');
            }
        }
    };

    const showDetails = (publisher) => {
        setSelectedPublisher(publisher);
    };

    const columns = [
        {
            name: 'Name',
            selector: row => row.name,
            sortable: true,
        },
        {
            name: 'Email',
            selector: row => row.email,
        },
        {
            name: 'Company',
            selector: row => row.company,
        },
        {
            name: 'Phone',
            selector: row => row.phone,
        },
        {
            name: 'Password',
            selector: row => row.password,
        },
        {
            name: 'Invoice Enabled',
            cell: row => row.isInvoiceEnable ? <FaCheck className="text-green-500" /> : <FaTimes className="text-red-500" />,
            center: true
        },
    ];

    const ExpandableRow = ({ data }) => (
        <div className="flex flex-wrap gap-2 mt-2 px-2">
            <button
                onClick={() => toggleInvoiceStatus(data.id, data.isInvoiceEnable)}
                className="bg-blue-500 hover:bg-blue-600 text-white text-sm px-4 py-2 rounded-md flex items-center gap-1"
            >
                {data.isInvoiceEnable ? <FaToggleOff /> : <FaToggleOn />}
                {data.isInvoiceEnable ? 'Disable Invoice' : 'Enable Invoice'}
            </button>

            {!data.isApprovedByAdmin && (
                <button
                    onClick={() => approvePublisher(data.id)}
                    className="bg-green-500 hover:bg-green-600 text-white text-sm px-4 py-2 rounded-md flex items-center gap-1"
                >
                    <FaThumbsUp /> Approve
                </button>
            )}

            <button
                onClick={() => showDetails(data)}
                className="bg-yellow-500 hover:bg-yellow-600 text-white text-sm px-4 py-2 rounded-md flex items-center gap-1"
            >
                <FaEye /> Details
            </button>

            <button
                onClick={() => deletePublisher(data.id)}
                className="bg-red-500 hover:bg-red-600 text-white text-sm px-4 py-2 rounded-md flex items-center gap-1"
            >
                <FaTrash /> Delete
            </button>
        </div>
    );

    return (
        <div className="p-6 bg-gradient-to-br from-gray-50 to-indigo-100 min-h-screen">
            <ToastContainer />
            <div className="max-w-7xl mx-auto bg-white p-6 rounded-xl shadow-xl">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Publishers</h2>

                {/* Search Box */}
                <div className="flex justify-end mb-4">
                    <input
                        type="text"
                        placeholder="Search by Name, Email, Company, or Phone..."
                        value={searchTerm}
                        onChange={handleSearch}
                        className="w-80 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400"
                    />
                </div>

                {/* Table */}
                <DataTable
                    columns={columns}
                    data={filteredPublishers}
                    progressPending={loading}
                    pagination
                    highlightOnHover
                    striped
                    responsive
                    persistTableHead
                    expandableRows
                    expandableRowsComponent={ExpandableRow}
                    noHeader
                />

                {/* Details Panel */}
                {selectedPublisher && (
                    <div className="mt-6 p-5 border rounded-lg shadow-md bg-gray-100">
                        <h2 className="text-lg font-bold mb-3">Publisher Details</h2>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="font-semibold">Name:</label>
                                <p>{selectedPublisher.name}</p>
                            </div>
                            <div>
                                <label className="font-semibold">Email:</label>
                                <p>{selectedPublisher.email}</p>
                            </div>
                            <div>
                                <label className="font-semibold">Company:</label>
                                <p>{selectedPublisher.company}</p>
                            </div>
                            <div>
                                <label className="font-semibold">Phone:</label>
                                <p>{selectedPublisher.phone}</p>
                            </div>
                            <div>
                                <label className="font-semibold">Is Invoice Enabled:</label>
                                <p>{selectedPublisher.isInvoiceEnable ? 'Yes' : 'No'}</p>
                            </div>
                        </div>
                        <button
                            onClick={() => toggleInvoiceStatus(selectedPublisher.id, selectedPublisher.isInvoiceEnable)}
                            className="mt-4 bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
                        >
                            {selectedPublisher.isInvoiceEnable ? 'Disable Invoice' : 'Enable Invoice'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PublishersTable;
